import React from "react";

interface IFieldProps {
  label: string;
  value: string;
  /** Tooltip / screen-reader text, e.g. why the control is disabled. */
  hint?: string;
  /** Disabled placeholder (greyed) rather than a read-only read-out. */
  disabled?: boolean;
  className?: string;
}

/** Mid-fi `.sel` box: small uppercase label + value. Shared by the filter bars' static fields. */
export const MID_FI_FIELD_CLASS =
  "flex items-center gap-2.5 rounded-[9px] border border-mf-border bg-white px-3.5 py-2 text-[12.5px] font-semibold";

export const MidFiFieldLabel = ({ children }: { children: React.ReactNode }) => (
  <small className="text-[9.5px] font-bold uppercase tracking-[.06em] text-mf-faint">{children}</small>
);

/** A filter-bar field that shows a value but can't be changed yet (disabled placeholder or read-out). */
export const FilterField = ({ label, value, hint, disabled, className = "" }: IFieldProps) => (
  <div
    className={`${MID_FI_FIELD_CLASS} ${disabled ? "text-mf-faint" : "text-mf-ink"} ${className}`}
    title={hint}
    aria-disabled={disabled ? "true" : undefined}
  >
    <MidFiFieldLabel>{label}</MidFiFieldLabel>
    <span>{value}</span>
    {hint ? <span className="sr-only">{hint}</span> : null}
  </div>
);

interface IProps {
  /** Property control, first in the bar per the low-fi sheets. */
  property?: React.ReactNode;
  /** Label for the asset filter, shown disabled until asset-level data exists. */
  assetsLabel: string;
  /** Why the asset filter is disabled (tooltip / screen-reader text). */
  assetsHint: string;
  /** Range + compare controls. */
  children: React.ReactNode;
  /** Read-only basis read-out at the right (mid-fi), e.g. "Session feed kWh · pre-credit bill rate $". */
  basis?: string | null;
}

// Page-level filter bar from the detail sheets (4019): Property, Assets, range / compare, then Basis.
// The asset filter is a disabled placeholder until an asset list exists (e.g. SWTCH ports / charger
// level); Basis is read-only while each page has a single basis.
export const DetailFilterBar = ({ property, assetsLabel, assetsHint, children, basis }: IProps) => (
  <div className="flex flex-wrap items-center gap-3.5" aria-label="Page filters">
    {property}
    <FilterField label="Assets" value={assetsLabel} hint={assetsHint} disabled />
    {children}
    {basis ? (
      <FilterField
        label="Basis"
        value={basis}
        hint="Only one basis is available for this page so far."
        className="lg:ml-auto"
      />
    ) : null}
  </div>
);
